import { useCallback, useEffect, useRef, useState } from "react";
import * as React from "react";
import Webcam from "react-webcam";

import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { dataUrlToFile } from "utils/common";

type WebcamPickerProps = {
  container: {
    width?: number;
    height?: number;
  };
  videoConstraints: {
    width: number;
    height: number;
  };
  onCapture: (file: File) => void;
};

export default function WebcamPicker({
  container,
  videoConstraints,
  onCapture
}: WebcamPickerProps) {
  const webcamRef = useRef<Webcam>(null);
  const handleCapture = useCallback(
    (getScreenshot: Webcam["getScreenshot"]) => async () => {
      const imageSrc = getScreenshot();
      const result = await dataUrlToFile(imageSrc || "", "webcam-photo.png");
      onCapture(result);
    },
    [onCapture]
  );

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);

  useEffect(() => {
    navigator.mediaDevices
      .enumerateDevices()
      .then((deviceList) => {
        const videoDevices = deviceList.filter(
          (device) => device.kind === "videoinput"
        );
        setDevices(videoDevices);
        if (videoDevices.length > 0) {
          setSelectedDeviceId(videoDevices[0].deviceId); // Default to the first camera
        }
      })
      .catch((err) => console.error("Error fetching devices:", err));
  }, []);

  const handleCameraChange = (event: SelectChangeEvent<string | null>) => {
    setSelectedDeviceId(event.target.value);
  };

  return (
    <>
      {devices.length > 1 && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Camera</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            value={selectedDeviceId}
            label="Select Camera"
            onChange={handleCameraChange}
          >
            {devices?.map((device) => {
              return (
                <MenuItem value={device.deviceId} key={device.deviceId}>
                  {device.label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      )}

      <Webcam
        audio={false}
        width={container.width}
        height={container.height}
        mirrored
        imageSmoothing
        ref={webcamRef}
        screenshotFormat="image/jpeg"
        videoConstraints={{
          deviceId: selectedDeviceId || undefined,
          width: videoConstraints.width,
          height: videoConstraints.height,
          facingMode: "user"
        }}
      >
        {/* @ts-ignore */}
        {({ getScreenshot }) => (
          <Button
            variant="contained"
            sx={{
              mt: 2
            }}
            onClick={handleCapture(getScreenshot)}
          >
            Take a Photo
          </Button>
        )}
      </Webcam>
    </>
  );
}
